import { Image as ImageIcon, Receipt, Clock, Package } from "@phosphor-icons/react";
import CompletenessBadge from "./CompletenessBadge";
import ProductActionsMenu from "./ProductActionsMenu";

const fmt = (n, cur) => `${cur === "USD" ? "$" : "MX$"}${Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const cc = (p) => p.cost_currency || p.currency || "USD";
const sc = (p) => p.sale_currency || p.currency || "MXN";

function Price({ label, value, strong, testid }) {
  return (
    <div className="min-w-0" data-testid={testid}>
      <div className="text-[9px] uppercase tracking-widest text-zinc-500 font-semibold">{label}</div>
      <div className={`numeric truncate ${strong ? "text-sm font-bold text-zinc-900" : "text-xs text-zinc-700"}`}>{value}</div>
    </div>
  );
}

// Tarjeta de producto para pantallas chicas (la tabla no cabe en un teléfono).
export default function ProductMobileCard({ p, perms, on, canSeeCost, completeness, onFix, age, onPhotos, onName }) {
  const photos = (Array.isArray(p.photos) ? p.photos.filter(Boolean) : []);
  if (photos.length === 0 && p.photos_url) photos.push(p.photos_url);
  const cover = (p.cover_photo || "").trim() || photos[0];
  const low = p.stock <= p.low_stock_threshold;
  const canAct = perms.canEdit || perms.canSuggest || perms.canMakeMovement;

  return (
    <article className="border border-zinc-200 bg-white rounded-md p-3 space-y-3" data-testid={`product-card-${p.id}`}>
      <div className="flex gap-3">
        <button type="button" onClick={() => photos.length && onPhotos(photos)} className="shrink-0 w-16 h-16 rounded border border-zinc-200 bg-zinc-50 overflow-hidden relative flex items-center justify-center" data-testid={`card-photos-${p.id}`} title={photos.length ? `Ver ${photos.length} foto(s)` : "Sin fotos"}>
          {cover ? <img src={cover} alt="" className="w-full h-full object-cover" /> : <ImageIcon size={20} className="text-zinc-300" />}
          {photos.length > 1 && <span className="absolute bottom-0.5 right-0.5 bg-[#002FA7] text-white text-[9px] font-bold leading-none rounded-full px-1 py-[2px] min-w-[14px] text-center">{photos.length}</span>}
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex items-start gap-1">
            <button type="button" onClick={() => onName(p)} className="font-semibold text-sm text-left leading-snug text-zinc-900 hover:text-[#002FA7] line-clamp-2 flex-1 min-w-0" data-testid={`card-name-${p.id}`} title="Ver en qué notas de venta se ha vendido">
              {p.name}
            </button>
            {p.aesthetic_grade && (
              <span className={`shrink-0 inline-flex items-center justify-center w-5 h-5 rounded-sm text-[10px] font-heading font-extrabold ${p.aesthetic_grade === "A" ? "bg-emerald-100 text-emerald-700" : p.aesthetic_grade === "B" ? "bg-amber-100 text-amber-700" : "bg-red-100 text-[#E0000F]"}`}>{p.aesthetic_grade}</span>
            )}
            {canAct && <ProductActionsMenu product={p} perms={perms} on={on} align="end" testid={`card-menu-${p.id}`} />}
          </div>
          <div className="text-[11px] text-zinc-500 mt-0.5 truncate">{[p.category, p.brand].filter(Boolean).join(" · ") || "—"}{p.sku ? <span className="font-mono-data"> · {p.sku}</span> : null}</div>
          {p.characteristics && <div className="text-xs text-zinc-500 mt-1 line-clamp-2 leading-snug">{p.characteristics}</div>}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-1.5">
        <span className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-sm border ${low ? "bg-red-50 text-[#E0000F] border-red-200" : "bg-zinc-50 text-zinc-800 border-zinc-200"}`} data-testid={`card-stock-${p.id}`}>
          <Package size={11} weight="bold" /> Stock {p.stock}
          {p.hospital_qty > 0 && <span className="ml-1 text-amber-700" title={`${p.hospital_qty} en taller`}>· 🏥{p.hospital_qty}</span>}
        </span>
        {completeness && <CompletenessBadge completeness={completeness} published={!!p.show_in_storefront} onFix={onFix} testid={`card-readiness-${p.id}`} />}
        {age && (
          <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded border text-[10px] font-semibold numeric ${age.tone}`}><Clock size={10} weight="bold" /> {age.text}</span>
        )}
        <button type="button" onClick={() => onName(p)} className="ml-auto inline-flex items-center gap-1 text-[11px] text-zinc-500 hover:text-[#002FA7]" data-testid={`card-sales-${p.id}`}><Receipt size={12} weight="duotone" /> Ventas</button>
      </div>

      <div className={`grid gap-2 ${canSeeCost ? "grid-cols-3" : "grid-cols-3"}`}>
        <Price label="Tienda" value={fmt(p.store_price, sc(p))} strong testid={`card-store-${p.id}`} />
        <Price label="Reventa" value={fmt(p.reseller_price, sc(p))} />
        <Price label="Distribuidor" value={fmt(p.marketplace_price, sc(p))} />
        {canSeeCost && <Price label="Costo" value={fmt(p.cost, cc(p))} testid={`card-cost-${p.id}`} />}
        {canSeeCost && <Price label="Precio mínimo" value={fmt(p.min_price, sc(p))} />}
        {canSeeCost && (
          <Price label="Alta" value={p.created_at ? new Date(p.created_at).toLocaleDateString("es-MX", { day: "2-digit", month: "short", year: "2-digit" }) : "—"} />
        )}
      </div>
    </article>
  );
}
